/* eslint-disable react/prop-types */
import './index.scss';
import ModalBase from '.';
import { DataSearchForm, ModalProps } from '../../types';
import { useEffect, useMemo, useState } from 'react';
import { Input, Select } from '@piggytechng/component-library';
import { OverideButtonStyle, OverideInputStyle, OverideSelectStyle } from '../../helpers/utils';
import { validateDataSearchForm } from '../../helpers/utils/validators';

function ModalSearch({ searchDataBy, modalIsOpen, setIsOpen, searchContext, data }: ModalProps) {
  function closeModal() {
    setIsOpen(false);
  }

  const SelectStyle = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };

  const [searchTerms, setSearchTerm] = useState({
    users: {
      query: '',
      activeFilter: '',
    },
    usdrequests: {
      search: '',
    },
    usdrequestsHeld: {
      userId: '',
    },
    withdrawalManager: {
      query: '',
      queryFilter: '',
    },
    admins: {
      filter: '',
      value: '',
    },
  });

  const modalTile = useMemo(() => {
    switch (searchContext) {
      case 'search-users':
        return 'Search Users';
      case 'search-usd-requests':
        return 'Search USD Requests';
      case 'search-withdrawal-manager':
        return 'Search Withdrawal Manager';
      case 'search-admins':
        return 'Search Admins';
      default:
        return 'Search';
    }
  }, [searchContext]);

  const modalType = useMemo(() => {
    switch (searchContext) {
      case 'search-users':
        return 'users';
      case 'search-usd-requests':
        return 'usdrequests';
      case 'search-usd-requests-held':
        return 'usdrequestsHeld';
      case 'search-withdrawal-manager':
        return 'withdrawalManager';
      case 'search-admins':
        return 'admins';
      default:
        return undefined;
    }
  }, [searchContext]);

  const [options] = useState({
    userIdentifier: [
      { label: 'User ID', value: 'userId' },
      { label: 'Email', value: 'email' },
      { label: 'Phone Number', value: 'phoneNo' },
      { label: 'Flex Account Number', value: 'nuban' },
      { label: 'Bank', value: 'bank' },
      { label: 'BVN', value: 'bvn' },
    ],

    withdrawalManagerIdentifier: [
      { label: 'Withdrawal ID', value: 'withdrawalId' },
      { label: 'User ID', value: 'userId' },
      { label: 'Account Number', value: 'accountNumber' },
      { label: 'Amount', value: 'amount' },
      { label: 'Reference', value: 'reference' },
      { label: 'Email', value: 'email' },
      { label: 'Phone Number', value: 'phoneNumber' },
    ],

    usdRequestIdentifier: [
      { label: 'User ID', value: 'userId' },
      { label: 'Email', value: 'email' },
    ],

    usdRequestHeldIdentifier: [{ label: 'User ID', value: 'userId' }],

    adminIdentifier: [
      { label: 'User ID', value: 'userId' },
      { label: 'Email', value: 'email' },
      { label: 'Phone Number', value: 'phone' },
    ],
  });

  const mapDataValue = (data: any) => {
    if (!data || !modalType) return {};
    const values = {} as any;
    for (const key in searchTerms[modalType as keyof DataSearchForm]) {
      values[key as keyof DataSearchForm[typeof modalType]] =
        data[key] ??
        searchTerms[modalType as keyof DataSearchForm][key as keyof DataSearchForm[typeof modalType]] ??
        '';
    }
    return values;
  };

  const searchByLabel = useMemo(() => {
    switch (searchContext) {
      case 'search-users':
        if (!searchTerms.users.activeFilter) {
          return 'Select a filter to search';
        }
        return `Enter  ${options.userIdentifier.find((option) => option.value === searchTerms.users.activeFilter)?.label}`;
      case 'search-withdrawal-manager':
        if (!searchTerms.withdrawalManager.queryFilter) {
          return 'Select a filter to search';
        }
        return `Enter  ${options.withdrawalManagerIdentifier.find((option) => option.value === searchTerms.withdrawalManager.queryFilter)?.label}`;
      case 'search-usd-requests-held':
        if (!searchTerms.usdrequestsHeld.userId) {
          return 'Select a filter to search';
        }
        return `Enter  ${options.usdRequestHeldIdentifier.find((option) => option.value === searchTerms.usdrequestsHeld.userId)?.label}`;
      default:
        return 'Search value is required';
    }
  }, [searchTerms, searchContext]);

  const canApply = useMemo(() => {
    return JSON.stringify(searchTerms[modalType as keyof DataSearchForm]) !== JSON.stringify(mapDataValue(data));
  }, [searchTerms, data]);

  const canClear = useMemo(() => {
    switch (searchContext) {
      case 'search-users':
        return searchTerms.users.activeFilter !== '' || searchTerms.users.query !== '';
      case 'search-usd-requests':
        return searchTerms.usdrequests.search !== '';
      case 'search-usd-requests-held':
        return searchTerms.usdrequestsHeld.userId !== '';
      case 'search-withdrawal-manager':
        return searchTerms.withdrawalManager.queryFilter !== '' || searchTerms.withdrawalManager.query !== '';
      default:
        return false;
    }
  }, [searchTerms]);

  const isValidData = useMemo(() => {
    return validateDataSearchForm(mapDataValue(data), modalType, false);
  }, [data, modalType]);

  const resetSearchTerms = () => {
    setSearchTerm({
      users: {
        query: '',
        activeFilter: '',
      },
      usdrequests: {
        search: '',
      },
      usdrequestsHeld: {
        userId: '',
      },
      withdrawalManager: {
        query: '',
        queryFilter: '',
      },
      admins: {
        filter: '',
        value: '',
      },
    });
    searchDataBy &&
      searchDataBy(
        {
          users: { query: '', activeFilter: '' },
          usdrequests: { search: '' },
          usdrequestsHeld: { userId: '' },
          withdrawalManager: { query: '', queryFilter: '' },
          admins: { filter: '', value: '' },
        },
        !isValidData,
      );

    closeModal();
  };

  const handleSearch = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    if (validateDataSearchForm(searchTerms[modalType as keyof DataSearchForm], modalType)) {
      searchDataBy && searchDataBy({ ...data, ...searchTerms });
      closeModal();
    }
  };

  useEffect(() => {
    if (modalIsOpen && data) {
      setSearchTerm((prev) => ({
        ...prev,
        [modalType as any]: {
          ...mapDataValue(data),
        },
      }));
    }
  }, [modalIsOpen, data]);
  return (
    <ModalBase modalIsOpen={modalIsOpen} setIsOpen={setIsOpen}>
      <div className={`modal-search`}>
        <div className="modal-search__header">
          <h2>{modalTile}</h2>

          <button onClick={closeModal}>&times;</button>
        </div>
        <form className="modal-search__body" onSubmit={handleSearch}>
          {(modalType === 'users' || modalType === 'withdrawalManager') && (
            <>
              <Select
                {...SelectStyle}
                label={`Search ${modalType === 'users' ? 'users' : 'withdrawal requests'} by`}
                placeholder="Select search criteria"
                options={modalType === 'users' ? options.userIdentifier : options.withdrawalManagerIdentifier}
                value={
                  modalType === 'users' ? searchTerms.users.activeFilter : searchTerms.withdrawalManager.queryFilter
                }
                onChange={(e) =>
                  setSearchTerm({
                    ...searchTerms,
                    [modalType]: {
                      ...searchTerms[modalType],
                      [modalType === 'users' ? 'activeFilter' : 'queryFilter']: e,
                    },
                  })
                }
                height="57px"
                backgroundColor="#F7F7F7"
                labelStyle={{
                  color: '#666766',
                }}
              />
              <Input
                {...OverideInputStyle}
                labelStyle={{
                  color: '#666766',
                }}
                type="text"
                label="Search value"
                placeholder={searchByLabel}
                value={searchTerms[modalType].query}
                onChange={(e) =>
                  setSearchTerm({ ...searchTerms, [modalType]: { ...searchTerms[modalType], query: e.target.value } })
                }
              />
            </>
          )}

          {(modalType === 'usdrequests' || modalType === 'usdrequestsHeld') && (
            <>
              <Select
                {...SelectStyle}
                label="Search USD requests by"
                placeholder="Select search criteria"
                options={modalType === 'usdrequests' ? options.usdRequestIdentifier : options.usdRequestHeldIdentifier}
                height="57px"
                backgroundColor="#F7F7F7"
                labelStyle={{
                  color: '#666766',
                }}
              />
              <Input
                {...OverideInputStyle}
                labelStyle={{
                  color: '#666766',
                }}
                type="text"
                label="Search value"
                placeholder={searchByLabel}
                value={
                  modalType === 'usdrequests' ? searchTerms.usdrequests.search : searchTerms.usdrequestsHeld.userId
                }
                onChange={(e) =>
                  setSearchTerm({
                    ...searchTerms,
                    [modalType]: {
                      ...searchTerms[modalType],
                      [modalType === 'usdrequests' ? 'search' : 'userId']: e.target.value,
                    },
                  })
                }
              />
            </>
          )}

          {modalType === 'admins' && (
            <>
              <Select
                {...SelectStyle}
                label="Search admins by"
                placeholder="Select search criteria"
                options={options.adminIdentifier}
                height="57px"
                backgroundColor="#F7F7F7"
                labelStyle={{
                  color: '#666766',
                }}
                value={searchTerms.admins.filter}
                onChange={(e) =>
                  setSearchTerm({
                    ...searchTerms,
                    admins: {
                      ...searchTerms.admins,
                      filter: e,
                    },
                  })
                }
              />
              <Input
                {...OverideInputStyle}
                labelStyle={{
                  color: '#666766',
                }}
                type="text"
                label="Search value"
                placeholder={searchByLabel}
                value={searchTerms.admins.value}
                onChange={(e) =>
                  setSearchTerm({ ...searchTerms, admins: { ...searchTerms.admins, value: e.target.value } })
                }
              />
            </>
          )}

          <div className="modal-search__actions">
            <button className="modal-search__actions-reject" type="button" onClick={() => resetSearchTerms()}>
              {canClear ? 'Clear' : 'Close'}
            </button>

            {canApply && (
              <button {...OverideButtonStyle} className="modal-search__actions-resolve">
                Apply
              </button>
            )}
          </div>
        </form>
      </div>
    </ModalBase>
  );
}

export default ModalSearch;
